import api from './api';

export interface SessionItem {
  id: number;
  item_type: 'course' | 'exercise' | 'video' | 'newpage';
  course: number | null;
  course_title?: string;
  exercise: number | null;
  exercise_pattern?: string;
  video: number | null;
  video_title?: string;
  video_thumbnail_url?: string;
  order: number;
  composite_config?: {
    composite_id: number;
    pool: number[];
    count: number;
    group_id: string;
  } | null;
}

export interface Session {
  id: number;
  name: string;
  description?: string;
  reasons?: string[];
  is_public: boolean;
  is_deleted: boolean;
  tags: string[];
  created_by: number;
  created_by_name: string;
  created_at: string;
  updated_at: string;
  can_edit: boolean;
  order: number;
  container: number | null;
  items: SessionItem[];
  preview_pdf_url?: string | null;
}

class SessionService {
  async getAll(params?: any): Promise<Session[]> {
    const response = await api.get('/sessions/', { params });
    if (response.data && response.data.results) {
      return response.data.results;
    }
    return response.data;
  }

  async getSession(id: number): Promise<Session> {
    const response = await api.get(`/sessions/${id}/`);
    return response.data;
  }

  async create(data: Partial<Session>): Promise<Session> {
    const response = await api.post('/sessions/', data);
    return response.data;
  }

  async update(id: number, data: Partial<Session>): Promise<Session> {
    const response = await api.patch(`/sessions/${id}/`, data);
    return response.data;
  }

  async delete(id: number): Promise<void> {
    await api.delete(`/sessions/${id}/`);
  }

  async duplicate(id: number): Promise<Session> {
    const response = await api.post(`/sessions/${id}/duplicate/`);
    return response.data;
  }

  async reorder(items: { id: number; order: number }[]): Promise<void> {
    await api.post('/sessions/reorder/', { items });
  }

  async addItem(sessionId: number, data: { item_type: 'course' | 'exercise' | 'video' | 'newpage'; course_id?: number; exercise_id?: number; video_id?: number; order?: number; composite_config?: any }): Promise<SessionItem> {
    const response = await api.post(`/sessions/${sessionId}/add_item/`, data);
    return response.data;
  }

  async removeItem(sessionId: number, itemId: number): Promise<void> {
    await api.delete(`/sessions/${sessionId}/remove_item/`, { data: { item_id: itemId } });
  }

  async reorderItems(sessionId: number, items: { id: number; order: number }[]): Promise<void> {
    await api.post(`/sessions/${sessionId}/reorder_items/`, { items });
  }

  async generatePreview(sessionId: number): Promise<string> {
    const response = await api.post(`/sessions/${sessionId}/preview_pdf/`, {}, {
      responseType: 'blob',
    });
    // Create blob URL from response (PDF is also saved on the server)
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    return url;
  }

  async generatePartialPreview(sessionId: number, itemIds: number[]): Promise<string> {
    const response = await api.post(`/sessions/${sessionId}/partial_preview_pdf/`, {
      item_ids: itemIds
    }, {
      responseType: 'blob',
    });
    // Create blob URL from response
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    return url;
  }

  async refondre(sessionId: number): Promise<{ session: Session; regenerated: number; errors: any[] }> {
    const response = await api.post(`/sessions/${sessionId}/refondre/`);
    return response.data;
  }

  async generateSolutionsPdf(sessionId: number): Promise<string> {
    const response = await api.post(`/sessions/${sessionId}/solutions_pdf/`, {}, {
      responseType: 'blob',
    });
    const blob = new Blob([response.data], { type: 'application/pdf' });
    return URL.createObjectURL(blob);
  }

  async getPreferences(): Promise<any> {
    const response = await api.get('/sessions/preferences/');
    return response.data;
  }

  async updatePreferences(prefs: any): Promise<any> {
    const response = await api.patch('/sessions/preferences/', prefs);
    return response.data;
  }

  async getAvailableSessions(): Promise<Session[]> {
    const response = await api.get('/sessions/available/');
    return response.data;
  }
}

const sessionService = new SessionService();
export default sessionService;

